/**
 * 缝的查找、移除、插入（M4-01 / DESIGN.md §2.11）。
 *
 * 只实现**竖直缝**（一条从上到下、每行恰好一个像素的路径，移除/插入它
 * 改变宽度）。改变高度时调用方把图像转置后复用同一套竖直缝逻辑——
 * 缝本身是方向无关的算法，转置比另写一套横向版本更不容易出两份实现不一致的 bug。
 */

/** 每行一个 x 坐标：缝的路径。 */
export type Seam = Int32Array;

/**
 * 动态规划找最小能量竖直缝。
 *
 * `M[y][x] = energy[y][x] + min(M[y-1][x-1], M[y-1][x], M[y-1][x+1])`，
 * 边界（x=0 或 x=width-1）越界的候选按 +∞ 处理——不是 clamp，因为
 * 一条缝理应可以贴着图像边缘走，clamp 会人为压低边缘的路径代价。
 */
export function findMinSeam(energy: Float32Array, width: number, height: number): Seam {
  const cost = new Float32Array(width * height);
  const from = new Int8Array(width * height); // -1/0/+1：上一行选的哪个候选

  for (let x = 0; x < width; x++) cost[x] = energy[x]!;

  for (let y = 1; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let best = cost[(y - 1) * width + x]!;
      let choice = 0;
      if (x > 0 && cost[(y - 1) * width + x - 1]! < best) {
        best = cost[(y - 1) * width + x - 1]!;
        choice = -1;
      }
      if (x < width - 1 && cost[(y - 1) * width + x + 1]! < best) {
        best = cost[(y - 1) * width + x + 1]!;
        choice = 1;
      }
      cost[y * width + x] = best + energy[y * width + x]!;
      from[y * width + x] = choice;
    }
  }

  let bestX = 0;
  let bestCost = Infinity;
  const lastRow = (height - 1) * width;
  for (let x = 0; x < width; x++) {
    if (cost[lastRow + x]! < bestCost) {
      bestCost = cost[lastRow + x]!;
      bestX = x;
    }
  }

  const seam = new Int32Array(height);
  let x = bestX;
  for (let y = height - 1; y >= 0; y--) {
    seam[y] = x;
    if (y > 0) x += from[y * width + x]!;
  }
  return seam;
}

/** 移除一条竖直缝：宽度减一，每行把缝右侧的像素左移一格。 */
export function removeSeam(
  pixels: Uint8Array,
  width: number,
  height: number,
  seam: Seam,
): Uint8Array {
  const out = new Uint8Array((width - 1) * height * 4);
  for (let y = 0; y < height; y++) {
    const cut = seam[y]!;
    const srcRow = y * width * 4;
    const dstRow = y * (width - 1) * 4;
    // 缝左侧原样拷贝
    out.set(pixels.subarray(srcRow, srcRow + cut * 4), dstRow);
    // 缝右侧整体左移一个像素
    out.set(
      pixels.subarray(srcRow + (cut + 1) * 4, srcRow + width * 4),
      dstRow + cut * 4,
    );
  }
  return out;
}

/**
 * 插入一条竖直缝：宽度加一，在缝位置插入一个新像素（与相邻像素的均值），
 * 右侧整体右移一格。放大画布时用——插入的像素取自缝两侧的平均色，
 * 视觉上接近该处原有内容，比直接复制更不容易出现明显的重复条纹。
 */
export function insertSeam(
  pixels: Uint8Array,
  width: number,
  height: number,
  seam: Seam,
): Uint8Array {
  const out = new Uint8Array((width + 1) * height * 4);
  for (let y = 0; y < height; y++) {
    const cut = seam[y]!;
    const srcRow = y * width * 4;
    const dstRow = y * (width + 1) * 4;
    out.set(pixels.subarray(srcRow, srcRow + (cut + 1) * 4), dstRow);

    const leftAt = srcRow + cut * 4;
    const rightAt = srcRow + Math.min(cut + 1, width - 1) * 4;
    const insertAt = dstRow + (cut + 1) * 4;
    for (let c = 0; c < 4; c++) {
      out[insertAt + c] = Math.round((pixels[leftAt + c]! + pixels[rightAt + c]!) / 2);
    }

    out.set(
      pixels.subarray(srcRow + (cut + 1) * 4, srcRow + width * 4),
      dstRow + (cut + 2) * 4,
    );
  }
  return out;
}

/** 转置 RGBA8 图像（宽高互换），供横向缝复用竖直缝算法。 */
export function transposeRgba(pixels: Uint8Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const src = (y * width + x) * 4;
      const dst = (x * height + y) * 4;
      out[dst] = pixels[src]!;
      out[dst + 1] = pixels[src + 1]!;
      out[dst + 2] = pixels[src + 2]!;
      out[dst + 3] = pixels[src + 3]!;
    }
  }
  return out;
}

/** 转置蒙版（单通道 Float32Array 能量图，或 Uint8Array 蒙版均可用同一形状）。 */
export function transposeSingleChannel<T extends Float32Array | Uint8Array>(
  data: T,
  width: number,
  height: number,
): T {
  const out = new (data.constructor as new (n: number) => T)(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      (out as unknown as Float32Array)[x * height + y] = (data as unknown as Float32Array)[
        y * width + x
      ]!;
    }
  }
  return out;
}

/** 单通道版移除缝（用于同步收缩保护/移除蒙版）。 */
export function removeSeamChannel(
  data: Uint8Array,
  width: number,
  height: number,
  seam: Seam,
): Uint8Array {
  const out = new Uint8Array((width - 1) * height);
  for (let y = 0; y < height; y++) {
    const cut = seam[y]!;
    const srcRow = y * width;
    const dstRow = y * (width - 1);
    out.set(data.subarray(srcRow, srcRow + cut), dstRow);
    out.set(data.subarray(srcRow + cut + 1, srcRow + width), dstRow + cut);
  }
  return out;
}

/** 单通道版插入缝（插入值取缝两侧均值）。 */
export function insertSeamChannel(
  data: Uint8Array,
  width: number,
  height: number,
  seam: Seam,
): Uint8Array {
  const out = new Uint8Array((width + 1) * height);
  for (let y = 0; y < height; y++) {
    const cut = seam[y]!;
    const srcRow = y * width;
    const dstRow = y * (width + 1);
    out.set(data.subarray(srcRow, srcRow + cut + 1), dstRow);
    const left = data[srcRow + cut]!;
    const right = data[srcRow + Math.min(cut + 1, width - 1)]!;
    out[dstRow + cut + 1] = Math.round((left + right) / 2);
    out.set(data.subarray(srcRow + cut + 1, srcRow + width), dstRow + cut + 2);
  }
  return out;
}
